import { styled } from "@linaria/react";
import React, { useLayoutEffect, useState } from "react";
import Dropdown, { DropdownProps } from "@components/_Revamp/DropdownInput";
import { Input, InputProps } from "@components/_Revamp/Input/Input";
import { EverGreenStyle } from "@utils/Style/everGreen";
import { ITheme } from "src/theme-revamp";
import SwapButton from "@components/_Revamp/ListMyinfoChild/swapButton";

let currentProductInHash = "";

const ErrorMessage = styled.p`
  margin-top: 8px;
  color: #c50000;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
`;

const InputLabel = styled.label<InputLabelProps>`
  color: ${(props) => (props.labelColor ? props.labelColor : "#434343")};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.8;
  margin-bottom: 16px;
  span {
    display: flex;
    /* span {
      margin-left: 3px;
    } */
    img:first-child {
      margin-left: 4px;
      padding: 4px;
    }
  }
`;

const InputGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

export interface InputGroupProps {
  id: string;
  name?: string;
  label: string;
  noBluRequired?: boolean;
  inputType: "input" | "dropdown" | "swapbuttonname";
  placeholder?: string;
  errorMessage?: string;
  inputProps?: Omit<InputProps, "id" | "name" | "placeholder">;
  dropdownProps?: Omit<
    DropdownProps,
    | "id"
    | "name"
    | "bindLabel"
    | "bindId"
    | "margin"
    | "onChange"
    | "variant"
    | "placeholder"
  >;
  invalid?: boolean;
  inputPropsForInputBox?: {
    inputMode:
      | "text"
      | "none"
      | "search"
      | "email"
      | "tel"
      | "url"
      | "numeric"
      | "decimal"
      | undefined;
    inputType:
      | "number"
      | "normal"
      | "numeric"
      | "decimal"
      | "alphanumeric"
      | "email"
      | "tel"
      | undefined;
  };
  labelColor?: string;
  theme?: ITheme;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  pattern?: string;
  onBlurShowError?: boolean;
  readOnly?: boolean;
  readOnlyColor?: string;
  swap?: string;
  swapModalOpen?: (e: any) => void;
  mobCode?: string;
  textAlign?: "left" | "center" | "right" | undefined;
  swapClassname?: string;
  swapButtonOnClick?: (e: any) => void;
  LabelToolTip?: () => JSX.Element;
}
export interface InputLabelProps {
  labelColor?: string;
}

export const InputGroup = React.forwardRef(
  (
    {
      id,
      name,
      label,
      inputType,
      inputPropsForInputBox,
      theme,
      invalid,
      placeholder,
      errorMessage,
      dropdownProps,
      inputProps,
      onChange,
      onBlur,
      onFocus,
      labelColor,
      pattern,
      onBlurShowError = false,
      noBluRequired = false,
      readOnly,
      readOnlyColor,
      swap,
      mobCode,
      textAlign,
      swapClassname,
      swapButtonOnClick,
      LabelToolTip,
    }: InputGroupProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    function getCurrentProductInHash() {
      const currentHash = globalThis.location.hash.split("/")[1];
      switch (currentHash) {
        case "evergreen":
          currentProductInHash = EverGreenStyle.colors.primary;
          break;
        default:
          currentProductInHash = "";
      }
    }
    useLayoutEffect(() => {
      getCurrentProductInHash();
    }, []);

    const [isBlur, setBlur] = useState<boolean>(!onBlurShowError);

    const onInputChange = (e: any) => {
      if (onChange) {
        onChange(e);
      }
    };
    const onInputBlur = (e: any) => {
      if (onBlur) {
        onBlur(e);
      }
      if (onBlurShowError) {
        setBlur(true);
      }
    };
    const onInputFocus = (e: any) => {
      if (onFocus) {
        onFocus(e);
      }
      if (onBlurShowError) {
        setBlur(false);
      }
    };

    return (
      <InputGroupContainer>
        <InputLabel labelColor={labelColor} htmlFor={id}>
          <span>{label}</span>
        </InputLabel>
        {inputType === "input" && (
          <Input
            inputMode={inputPropsForInputBox?.inputMode}
            typeInput={inputPropsForInputBox?.inputType}
            ref={ref}
            id={id}
            name={name}
            themes={theme}
            placeholder={placeholder}
            invalid={invalid && isBlur}
            textAlign={textAlign ? textAlign : "center"}
            onFocus={onInputFocus}
            onChange={onInputChange}
            onBlur={onInputBlur}
            pattern={pattern}
            margin="0"
            {...inputProps}
            readOnly={readOnly}
            readOnlyColor={readOnlyColor}
            swap={swap}
            mobCode={mobCode}
          />
        )}
        {inputType === "swapbuttonname" && (
          <SwapButtonContainer className={swapClassname}>
            <Input
              inputMode={inputPropsForInputBox?.inputMode}
              typeInput={inputPropsForInputBox?.inputType}
              ref={ref}
              id={id}
              name={name}
              themes={theme}
              placeholder={placeholder}
              invalid={invalid && isBlur}
              textAlign={textAlign ? textAlign : "center"}
              onFocus={onInputFocus}
              onChange={onInputChange}
              onBlur={onInputBlur}
              pattern={pattern}
              margin="0"
              {...inputProps}
              readOnly={readOnly}
              readOnlyColor={readOnlyColor}
              swap={swap}
              mobCode={mobCode}
            />
            <SwapButton onClick={swapButtonOnClick} />
          </SwapButtonContainer>
        )}
        {inputType === "dropdown" && (
          <Dropdown
            placeholder={placeholder}
            name={name}
            {...dropdownProps!}
            theme={theme}
            variant="default"
            bindLabel="label"
            bindId="id"
            margin="0"
            onFocus={onInputFocus}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        )}

        {invalid && errorMessage && isBlur && !noBluRequired && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
        {invalid && errorMessage && noBluRequired && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
      </InputGroupContainer>
    );
  }
);

InputGroup.defaultProps = {
  inputType: "input",
};

const SwapButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  /* The input's Container wrapper: take remaining space and allow it to
     shrink so long names truncate inside the input instead of being
     covered by the swap button. min-width: 0 is required for the flex item
     to shrink below its content width. */
  & > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
    width: unset;
  }
  & > div:first-child input {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  /* Keep the swap button at its natural size, never shrinking or overlapping. */
  & > button {
    flex: 0 0 auto;
  }
`;
